import { useMemo } from "react";

import { canDo, canReach, type Area, type ExtraSwitch } from "@pharmacy/domain";

import { useSession } from "./session";

/**
 * What to draw for this person (guide 4.6), from the shared rules in
 * @pharmacy/domain, so the web and the server give the same answer.
 *
 * Drawing only. The server checks every request again and is the only check
 * that counts: a page that offers something the server refuses shows the
 * server's (translated) refusal.
 */
export interface Can {
  /** May they open this section? `reach("pos")` → show Sales. */
  reach: (area: Area) => boolean;
  /** May they use this control inside a page? `do("seeCostPrice")` → show costs. */
  do: (action: ExtraSwitch) => boolean;
  /** Owners (role admin): Staff, Branches, Billing, Pharmacy account… */
  isOwner: boolean;
}

const NOBODY: Can = { reach: () => false, do: () => false, isOwner: false };

export function useCan(): Can {
  const { state } = useSession();
  const me = state.status === "signedIn" ? state.me : null;

  return useMemo<Can>(() => {
    if (!me) return NOBODY;
    const who = { role: me.role, access: me.access };
    return {
      reach: (area) => canReach(area, who),
      do: (action) => canDo(action, who),
      isOwner: me.role === "admin",
    };
  }, [me]);
}
